import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [isFavorited, setIsFavorited] = useState(false);
  const [readingMode, setReadingMode] = useState<'scroll' | 'popup' | 'page'>('scroll');

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到 - 女神漫画"
          description="该漫画可能已被删除或不存在，返回女神漫画首页浏览更多精彩漫画。"
          canonicalUrl="https://nvshenmanhua.com"
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-6">该漫画可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = comic.category === 'korean' ? '韩漫' : comic.category === 'japanese' ? '日漫' : '国漫';
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.subCategory === comic.subCategory))
    .slice(0, 6);

  const comicSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${comic.title} - 女神漫画`,
    "description": comic.description,
    "author": {
      "@type": "Person",
      "name": comic.author
    },
    "datePublished": "2026-01-01",
    "dateModified": comic.updatedAt,
    "url": `https://nvshenmanhua.com/comic/${comic.id}`,
    "inLanguage": "zh-Hans",
    "isPartOf": {
      "@type": "WebSite",
      "name": "女神漫画",
      "url": "https://nvshenmanhua.com"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首页",
          "item": "https://nvshenmanhua.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": categoryName,
          "item": `https://nvshenmanhua.com/category/${comic.category}`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": comic.title,
          "item": `https://nvshenmanhua.com/comic/${comic.id}`
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 女神漫画 | 免费全集漫画在线阅读`}
        description={`${comic.title}，作者${comic.author}，女神漫画提供免费全集在线阅读。${comic.description}`}
        keywords={`${comic.title},女神漫画,${categoryName},免费全集,漫画在线阅读,${comic.tags.join(',')}`}
        canonicalUrl={`https://nvshenmanhua.com/comic/${comic.id}`}
        ogType="article"
        schemaJson={comicSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                {categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    title={`${comic.title} - 女神漫画`}
                  />
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => setIsFavorited(!isFavorited)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      isFavorited
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${isFavorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-lg`}></i>
                    {isFavorited ? '已收藏' : '收藏追更'}
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === 'completed'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status === 'completed' ? '已完结' : '连载中'}
                </span>
                <span className="flex items-center gap-1 text-sm text-accent-600 font-medium">
                  <i className="ri-star-fill"></i> {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>{formatViews(comic.views)} 阅读
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-bookmark-line mr-1"></i>{formatViews(comic.favorites)} 收藏
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-xl bg-background-100">
                <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">阅读模式</h3>
                <div className="flex gap-2">
                  {([
                    { key: 'scroll', label: '下拉式', icon: 'ri-arrow-down-line' },
                    { key: 'popup', label: '弹窗', icon: 'ri-window-line' },
                    { key: 'page', label: '翻页', icon: 'ri-book-open-line' },
                  ] as const).map((mode) => (
                    <button
                      key={mode.key}
                      onClick={() => setReadingMode(mode.key)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                        readingMode === mode.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${mode.icon} text-lg`}></i>
                      {mode.label}
                    </button>
                  ))}
                </div>

                <div className="mt-6 p-8 rounded-xl bg-background-100 border border-background-200/70 min-h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    {readingMode === 'scroll' && (
                      <>
                        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                          <i className="ri-arrow-down-line text-2xl text-primary-600"></i>
                        </div>
                        <p className="text-foreground-900 font-medium mb-2">下拉式阅读模式</p>
                        <p className="text-sm text-foreground-500">向下滚动即可连续阅读漫画内容，流畅无间断</p>
                      </>
                    )}
                    {readingMode === 'popup' && (
                      <>
                        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                          <i className="ri-window-line text-2xl text-accent-600"></i>
                        </div>
                        <p className="text-foreground-900 font-medium mb-2">弹窗阅读模式</p>
                        <p className="text-sm text-foreground-500">全屏弹窗显示，沉浸式阅读体验</p>
                      </>
                    )}
                    {readingMode === 'page' && (
                      <>
                        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                          <i className="ri-book-open-line text-2xl text-secondary-600"></i>
                        </div>
                        <p className="text-foreground-900 font-medium mb-2">翻页阅读模式</p>
                        <p className="text-sm text-foreground-500">左右翻页，模拟纸质漫画阅读感受</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  章节列表（共{comic.chapters}话）
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                  {Array.from({ length: Math.min(comic.chapters, 40) }, (_, i) => comic.chapters - i).map((ch) => (
                    <button
                      key={ch}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <span>第{ch}话</span>
                      {ch === comic.chapters && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500 text-background-50">NEW</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} {...rc} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}